import {
	NAVIGABLE_ACCORDION_SECTION_CLASS_NAME,
	NAVIGABLE_SECTION_IDS,
	NAVIGATION_BAR_ID,
	SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT,
} from '@/components/content/ProductDetails/constants';
import { useNavigableSectionsContext } from '@/components/content/ProductDetails/context/NavigableSectionsContext';
import {
	calculateScrollToNavigableSectionTopAdjustment,
	getElementOffsetTop,
	getNavigableSectionElement,
} from '@/components/content/ProductDetails/utils';
import { useLocalization } from '@/data/Localization';
import { Box, SxProps, Tab, Tabs, Theme, useMediaQuery, useTheme } from '@mui/material';
import { forwardRef, useMemo, useState, useTransition } from 'react';

// Defines the vertical order of sections as they appear on the page
const NAVIGABLE_SECTIONS_ORDER = [
	NAVIGABLE_SECTION_IDS.PRODUCT_DETAILS,
	NAVIGABLE_SECTION_IDS.PRODUCT_BUY_BY_COLOR,
	NAVIGABLE_SECTION_IDS.PRODUCT_COMPARE,
	NAVIGABLE_SECTION_IDS.PRODUCT_SPECS,
	NAVIGABLE_SECTION_IDS.PRODUCT_REVIEWS,
];

export const NavigationBar = forwardRef(
	(
		{
			sx,
			productBarHeight,
			headerHeight,
			navigationBarHeight,
		}: {
			sx?: SxProps<Theme>;
			productBarHeight: number;
			headerHeight: number;
			navigationBarHeight: number;
		},
		ref: React.Ref<HTMLDivElement>
	) => {
		const theme = useTheme();
		const isSmScreen = useMediaQuery(theme.breakpoints.down('md'));

		const { sectionIds } = useNavigableSectionsContext();
		const [, startTransition] = useTransition();
		const [activeSectionId, setActiveSectionId] = useState(NAVIGABLE_SECTION_IDS.PRODUCT_DETAILS);

		/**
		 * Build labels for the navigation tabs and memoize them
		 * because the `NavigationBar` object is not stable.
		 */
		const { NavigationBar } = useLocalization('HDMProductDetail');
		const labelsUnstable = Object.fromEntries(
			Object.keys(NavigationBar).map((key) => [
				key,
				NavigationBar[key as keyof typeof NavigationBar].t(),
			])
		);
		const labels = useMemo(
			() =>
				labelsUnstable as {
					[key in NAVIGABLE_SECTION_IDS]: string | undefined;
				},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[JSON.stringify(labelsUnstable)]
		);

		/**
		 * Generate the tabs for the navigation bar, including only the sections
		 * that are present for the current product.
		 */
		const tabs = useMemo(() => {
			if (!sectionIds.size) return [];

			return NAVIGABLE_SECTIONS_ORDER.reduce(
				(acc, sectionId) => {
					const hasSection = sectionIds.has(sectionId);
					if (!hasSection) return acc;

					const label = labels[sectionId];

					return [...acc, { label, id: sectionId }];
				},
				[] as Array<{
					label: string | undefined;
					id: NAVIGABLE_SECTION_IDS;
				}>
			);
		}, [sectionIds, labels]);

		/**
		 * Scrolls the page to the target section when a tab is clicked.
		 *
		 * Adjusts for fixed elements such as the navigation bar, product bar, and header
		 * so that the section aligns correctly in the viewport.
		 */
		const onClickTab = ({ sectionId }: { sectionId: NAVIGABLE_SECTION_IDS }) => {
			const sectionElement = getNavigableSectionElement(sectionId);

			if (!sectionElement) {
				console.warn('No section element found for the sectionId', sectionId);
				return;
			}

			startTransition(() => {
				setActiveSectionId(sectionId);
			});

			const offsetTop = getElementOffsetTop(sectionElement);

			const topAdjustment = calculateScrollToNavigableSectionTopAdjustment({
				sectionId,
				isSmScreen,
				headerHeight,
				navigationBarHeight,
				productBarHeight,
			});

			const isAccordionSection = sectionElement.classList.contains(
				NAVIGABLE_ACCORDION_SECTION_CLASS_NAME
			);

			if (isAccordionSection) {
				const event = new CustomEvent(SCROLL_TO_NAVIGABLE_ACCORDION_SECTION_EVENT, {
					detail: {
						sectionId,
						topAdjustment,
					},
				});

				sectionElement.dispatchEvent(event);

				return;
			}

			window.scroll({
				top: offsetTop - topAdjustment,
				// If smooth scrolling does not work:
				// - Ensure the browser supports it and has it enabled.
				// - Check that energy saving mode is not preventing smooth scrolling.
				behavior: 'smooth',
			});
		};

		return (
			<Box
				id={NAVIGATION_BAR_ID}
				ref={ref}
				component="nav"
				sx={[
					(theme) => ({
						display: !tabs.length ? 'none' : 'flex',
						flexDirection: 'column',
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						paddingLeft: 3,
						paddingRight: 3,
						backgroundColor: 'white',
						transition: 'transform 0.15s ease-in-out, opacity 0.15s ease-in-out',
						zIndex: theme.zIndex.mobileStepper - 1, // One less than the header zIndex
						boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
					}),
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				{/* Added this box so we don't have a gap between the navigation bar and the product bar. */}
				{/* On some resolutions, there is an artifact gap between the navigation bar and the product bar. */}
				<Box
					sx={{
						height: productBarHeight,
					}}
				/>

				{tabs.length ? (
					<Tabs
						value={activeSectionId}
						variant="scrollable"
						scrollButtons="auto"
						indicatorColor="secondary"
						textColor="secondary"
					>
						{tabs.map(({ label, id }) => (
							<Tab
								label={label}
								value={id}
								key={id}
								onClick={() => onClickTab({ sectionId: id })}
								className="sticky__anchor-link"
							/>
						))}
					</Tabs>
				) : null}
			</Box>
		);
	}
);
